import React, { useState, useEffect } from 'react';
import { ProgressViewerStep } from './ProgressViewerStep/ProgressViewerStep';
import { Skeleton, SkeletonItem, mergeClasses } from '@fluentui/react-components';
import { useStyles } from './ProgressViewer.styles';
import { useProgressStepsData } from './useProgressStepsData';
import { BlueprintFlowData } from '../../types/flowScenario';
import { ProgressViewerActionButtonProps } from './ProgressViewerActionButton/ProgressViewerActionButton';

export type StepConfig = {
  title: string;
  description: string;
  actionButton?: ProgressViewerActionButtonProps;
};

export type StepStatus = 'Completed' | 'Current' | 'Upcoming';

export type LoadingStatus = 'idle' | 'pending' | 'resolved' | 'rejected';

export type ProgressViewerState = {
  blueprintFlowData: BlueprintFlowData | undefined;
  steps?: StepConfig[];
  currentStepIndex?: number;
  loadingStatus: LoadingStatus;
  error?: string | null;
};

export const ProgressViewer: React.FC<ProgressViewerState> = ({
  loadingStatus,
  error,
  blueprintFlowData,
}) => {
  const styles = useStyles();
  const { steps, currentStepIndex } = useProgressStepsData(blueprintFlowData, 'blueprint');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile size on mount and when window resizes
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check initially
    checkIfMobile();

    // Add resize listener
    window.addEventListener('resize', checkIfMobile);

    // Clean up event listener
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Set initial collapse state based on screen size
  useEffect(() => {
    setIsCollapsed(isMobile);
  }, [isMobile]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Find active step
  const activeStep = steps && currentStepIndex ? steps[currentStepIndex - 1] : null;

  return (
    <div className={styles.progressViewerContainer}>
      <div className={styles.progressViewerBox}>
        <div
          className={mergeClasses(
            styles.progressViewerTitle,
            currentStepIndex > steps.length && isCollapsed && styles.progressViewerTitleAllCompleted
          )}
        >
          <span className={styles.progressViewerTitleContent}>Journey</span>
          <button
            onClick={toggleCollapse}
            className={styles.collapseButton}
            aria-label={isCollapsed ? 'Expand journey steps' : 'Collapse journey steps'}
          >
            {isCollapsed ? <ExpandIcon /> : <CollapseIcon />}
          </button>
        </div>

        <div
          className={`${styles.stepsContainer} ${isCollapsed ? styles.collapsed : styles.expanded}`}
        >
          {/* Loading state */}
          {loadingStatus === 'pending' && (
            <Skeleton className={styles.progressViewerSkeleton}>
              {[...Array(6)].map((_, index) => (
                <SkeletonItem key={index} className={styles.progressViewerSkeletonItem} size={8} />
              ))}
            </Skeleton>
          )}

          {/* Expanded state - show all steps */}
          {!isCollapsed &&
            loadingStatus === 'resolved' &&
            steps.map((step, index) => (
              <ProgressViewerStep
                key={index}
                stepStatus={
                  index + 1 === currentStepIndex
                    ? 'Current'
                    : index + 1 < currentStepIndex
                      ? 'Completed'
                      : 'Upcoming'
                }
                title={step.title}
                description={step.description}
                actionButton={step.actionButton}
              />
            ))}

          {/* Collapsed state - show only active step */}
          {isCollapsed && loadingStatus === 'resolved' && activeStep && (
            <ProgressViewerStep
              stepStatus="Current"
              title={activeStep.title}
              description={activeStep.description}
              actionButton={activeStep.actionButton}
            />
          )}
        </div>
      </div>
      <div className={styles.privacyInsightDisclaimer}>
        <div className={styles.privacyInsightIcon}>
          <PrivacyIcon />
        </div>
        <div className={styles.privacyInsightContent}>100% safe & secure</div>
      </div>
    </div>
  );
};

const PrivacyIcon: React.FC = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect
        x="3.33105"
        y="6.6665"
        width="9.33722"
        height="7.33639"
        rx="2"
        stroke="black"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M7.99949 12.0023V10.6685"
        stroke="black"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M10.0004 12.0023V10.6684C10.0005 9.8591 9.51304 9.12947 8.76536 8.8198C8.01768 8.51013 7.15708 8.68142 6.58496 9.25378"
        stroke="black"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M5.99851 11.3354V12.0024"
        stroke="black"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M5.33105 6.66666V4.66582V4.66582C5.33105 3.19245 6.52546 1.99805 7.99883 1.99805V1.99805C9.47221 1.99805 10.6666 3.19245 10.6666 4.66582V4.66582V6.66666"
        stroke="black"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

const CollapseIcon: React.FC = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M12 6L8 10L4 6"
        stroke="black"
        strokeOpacity="0.5"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const ExpandIcon: React.FC = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 10L8 6L12 10"
        stroke="black"
        strokeOpacity="0.5"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
